import { computed, defineComponent, PropType } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { minLength, required } from "@vuelidate/validators";
import { Container } from "aurelia-dependency-injection";

import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../enums/enums";
import { formatDateInput, formatPhone } from "../../../common/vue-helpers/modifiers/value-modifier";
import type { IBranchResponse } from "../../vue-interfaces/i-branch";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { helperTestingAgency } from "../../../common/vue-helpers/testing-agency-check";

import FormSection from "../../vue-custom-elements/FormSection/FormSection.vue";
import PhoneNumber from "../../vue-custom-elements/PhoneNumber/PhoneNumber.vue";
import DatePicker from "../../vue-custom-elements/DatePicker/DatePicker.vue";
import CommentInput from "../../vue-custom-elements/CommentInput/CommentInput.vue";
import BranchHistory from "../../vue-dialogs/BranchHistory/BranchHistory.vue";
import AddBranch from "../../vue-dialogs/AddBranch/AddBranch.vue";

export default defineComponent({
    components: {
        FormSection,
        PhoneNumber,
        DatePicker,
        CommentInput
    },
    props: {
        inquiry: { type: Object, default: null },
        isLoading: { type: Boolean, default: false },
        branches: { type: Array as PropType<IBranchResponse[]>, default: null },
        isEdit: { type: Boolean, default: false }
    },
    data() {
        return {
            _permissionManager: null as PermissionManager,
            isPhoneNumberInValid: false,
            applicationEnum: ApplicationEnum,
            isHomeCareInquiry: true,
            dialogRef: null,
            canAddBranches: false as boolean,
            hasTestAgency: false as boolean,
            v$: useVuelidate(),
            formatDateInput,
            formatPhone
        };
    },
    validations() {
        return {
            inquiry: {
                contactFirstName: { required },
                contactLastName: { required },
                contactPhoneNumbers: [
                    {
                        number: { required, minLength: minLength(10) }
                    }
                ],
                locationId: { required },
                date: { required }
            }
        };
    },
    emits: ["updateBranch"],
    async created() {
        this._permissionManager = Container.instance.get(PermissionManager);
        this.hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
        this.canAddBranches = this._permissionManager.checkPermission(
            ParentPermissionEnum.inquiries,
            PermissionActionEnum.canAddBranches
        );
    },
    mounted() {
        this.$emit("updateBranch", this.inquiry.application);
        if (this.inquiry.application == ApplicationEnum.AgencyCore) {
            this.isHomeCareInquiry = false;
        }
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    methods: {
        selectedBranch(branchId: string) {
            let branchDetail: IBranchResponse = null;
            if (this.branches.length > 0) {
                branchDetail = this.branches.find((branch) => branch.id === branchId);
            }
            this.inquiry.accountId = branchDetail ? branchDetail.accountId : "";
        },
        typeUpdated(value: number[]) {
            let name = value[0];
            (this.inquiry as any)[name][0].type = Number(value[1]);
        },
        countryUpdated(value: string[]) {
            let name = value[0];
            (this.inquiry as any)[name][0].country = value[1];
        },
        resultUpdatedAppended(value: string) {
            this.inquiry.comment += value;
        },
        resultUpdated(value: string) {
            this.inquiry.comment = value;
        },
        changeDate(date: any, name: string) {
            this.inquiry.date = date;
        },
        setClientPhone(value: any) {
            this.inquiry.phoneNumbers[0].number = value.toString();
        },
        setContactPhone(value: any) {
            if (value.length >= 10) value = value.replace(/[^\d]/g, "");
            this.inquiry.contactPhoneNumbers[0].number = value.toString();
        },
        handleViewBranchHistory() {
            this.dialogRef = this.$dialog.open(BranchHistory, {
                props: {
                    modal: true,
                    closeOnEscape: false,
                    showHeader: false,
                    style: {
                        width: "80vw"
                    }
                },
                data: {
                    inquiryDetails: this.inquiry
                }
            });
        },
        handleAddBranch() {
            this.dialogRef = this.$dialog.open(AddBranch, {
                props: {
                    modal: true,
                    closeOnEscape: false,
                    showHeader: false,
                    style: {
                        width: "40vw"
                    }
                },
                data: {
                    inquiryDetails: this.inquiry
                }
            });
        }
    }
});
